import { lazy, Suspense } from 'react'
import { exportData } from './utils'
import { useToast } from './components/Toast'
import { setSentryUser } from './sentry'
import { useRouter } from './hooks/useRouter'
import { viewStateToPath } from './utils/urlRouter'

import { useAuth } from './hooks/useAuth'
import { useContacts } from './hooks/useContacts'
import { useTemplates } from './hooks/useTemplates'
import { useSettings } from './hooks/useSettings'
import { useProjects } from './hooks/useProjects'

import LoginScreen from './components/LoginScreen'
import Dashboard from './components/Dashboard'
import { DashboardSkeleton } from './components/shared/Skeleton'

// Retry wrapper for lazy imports — handles stale chunk hashes after deployments
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() => {
      return new Promise(resolve => setTimeout(resolve, 1000)).then(() => importFn())
    })
  )
}

const PasswordResetScreen = lazyRetry(() => import('./components/PasswordResetScreen'))
const NewProject = lazyRetry(() => import('./components/NewProject'))
const ProjectView = lazyRetry(() => import('./components/ProjectView'))
const ContactDirectory = lazyRetry(() => import('./components/ContactDirectory'))
const ReportsHub = lazyRetry(() => import('./components/ReportsHub'))
const PortfolioReport = lazyRetry(() => import('./components/PortfolioReport'))
const VendorInsuranceReport = lazyRetry(() => import('./components/VendorInsuranceReport'))
const OverdueBlockedReport = lazyRetry(() => import('./components/OverdueBlockedReport'))
const ProjectReport = lazyRetry(() => import('./components/ProjectReport'))
const ProjectDocumentation = lazyRetry(() => import('./components/ProjectDocumentation'))
const BudgetDetailReport = lazyRetry(() => import('./components/BudgetDetailReport'))
const GanttView = lazyRetry(() => import('./components/GanttView'))
const BudgetRiskReport = lazyRetry(() => import('./components/BudgetRiskReport'))
const ChangeOrdersReport = lazyRetry(() => import('./components/ChangeOrdersReport'))
const TemplateBuilder = lazyRetry(() => import('./components/TemplateBuilder'))
const SettingsPage = lazyRetry(() => import('./components/SettingsPage'))
const HelpPage = lazyRetry(() => import('./components/HelpPage'))
const ContractorDashboard = lazyRetry(() => import('./components/ContractorDashboard'))
const StakeholderView = lazyRetry(() => import('./components/StakeholderView'))

export default function App() {
  const { showToast } = useToast()

  // --- URL-based navigation ---
  const { view: routerView, params, navigate, replace } = useRouter()

  // --- Domain hooks ---
  const auth = useAuth()
  const contactsHook = useContacts(auth.session, showToast)
  const settings = useSettings(auth.session, showToast)
  const templates = useTemplates(auth.session, showToast)
  const projectsHook = useProjects(auth.session, showToast, {
    activeProjectId: params.projectId || null,
    setActivatingInvite: auth.setActivatingInvite,
    reloadFlag: auth.reloadFlag,
    userSettings: settings.userSettings,
    setContacts: contactsHook.setContacts,
    saveContactToSupabase: contactsHook.saveContactToSupabase,
    getUserDisplayName: settings.getUserDisplayName,
  })

  // Tag Sentry with subscription tier once settings load
  if (auth.session && settings.userSettings?.subscriptionTier) {
    setSentryUser(auth.session.user.id, auth.session.user.email, settings.userSettings.subscriptionTier)
  }

  function handleSignOut() {
    auth.handleSignOut()
    contactsHook.setContacts([])
    settings.reset()
    projectsHook.reset()
    replace("/")
  }

  // --- Loading / auth gates ---
  if (auth.authLoading || auth.activatingInvite || (auth.session && !projectsHook.projectsLoaded)) return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-navy text-white px-6 py-4">
        <div className="h-6 w-16 bg-white/10 rounded animate-pulse" />
      </header>
      <div className="max-w-5xl mx-auto p-6">
        <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-4" />
        <DashboardSkeleton />
      </div>
    </div>
  )

  if (!auth.session && !auth.passwordRecovery) return <LoginScreen />

  const suspenseFallback = (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-navy text-white px-6 py-4">
        <div className="h-6 w-16 bg-white/10 rounded animate-pulse" />
      </header>
      <div className="max-w-5xl mx-auto p-6">
        <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-4" />
        <DashboardSkeleton />
      </div>
    </div>
  )

  if (auth.passwordRecovery) return (
    <Suspense fallback={suspenseFallback}>
      <PasswordResetScreen onDone={() => { auth.setPasswordRecovery(false); navigate("/"); }} />
    </Suspense>
  )

  // Feature gate: redirect disabled views to safe fallback
  const effectiveView = (() => {
    if (routerView === "contacts" && !settings.enabledFeatures.contacts) return "dashboard"
    if (routerView === "reports" && !settings.enabledFeatures.reports) return "dashboard"
    if (routerView === "gantt" && !settings.enabledFeatures.gantt) return "project"
    if (routerView === "budget-detail" && !settings.enabledFeatures.budget) return "project"
    if (routerView === "budget-risk" && !settings.enabledFeatures.budget) return "dashboard"
    if (routerView === "change-orders-report" && settings.enabledFeatures.changeOrders === false) return "reports"
    if (routerView === "template-builder" && !settings.enabledFeatures.templates) return "settings"
    return routerView
  })()

  // Fix URL when feature gate redirects
  if (effectiveView !== routerView) {
    const redirectPath = (effectiveView === "project" && params.projectId)
      ? viewStateToPath(effectiveView, { projectId: params.projectId })
      : viewStateToPath(effectiveView, {})
    queueMicrotask(() => replace(redirectPath))
  }

  // Stakeholder-only users should only reach the Projects tab of the dashboard.
  // If the URL carries a tab param for any other admin tab, strip it.
  if (
    effectiveView === "dashboard" &&
    params.tab &&
    params.tab !== "projects" &&
    projectsHook.projectsLoaded &&
    projectsHook.projects.length > 0 &&
    projectsHook.projects.every(p => p.role === "stakeholder")
  ) {
    queueMicrotask(() => replace("/"))
  }

  // Invalid project ID in URL → redirect to dashboard
  const projectViews = ['project', 'gantt', 'budget-detail', 'project-report', 'project-documentation']
  if (projectViews.includes(effectiveView) && params.projectId && !projectsHook.activeProject) {
    queueMicrotask(() => {
      showToast("Project not found.", "warning")
      replace("/")
    })
  }

  // --- Dashboard ---
  if (effectiveView === "dashboard") return (
    <Dashboard projects={projectsHook.projects} contacts={contactsHook.contacts}
      enabledFeatures={settings.enabledFeatures}
      projectGroups={settings.projectGroups}
      session={auth.session}
      hasApiKey={settings.userSettings?.hasApiKey || false}
      companyProfile={settings.companyProfile}
      onApproveChangeOrder={projectsHook.approveChangeOrder}
      onUpdateTask={(projectId, taskId, updates) => {
        projectsHook.updateProject(projectId, p => ({
          ...p,
          tasks: p.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t),
        }))
      }}
      onCreateGroup={settings.createProjectGroup}
      onRenameGroup={settings.renameProjectGroup}
      onDeleteGroup={settings.deleteProjectGroup}
      onAssignGroup={(projectId, groupId) => {
        settings.assignProjectToGroup(projectId, groupId, projectsHook.projects).then(ok => {
          if (ok) projectsHook.setProjects(ps => ps.map(p => p.id === projectId ? { ...p, groupId: groupId || null } : p))
        })
      }}
      onOpen={id => navigate(viewStateToPath('project', { projectId: id }))}
      onNew={() => navigate('/projects/new')}
      onContacts={() => navigate('/contacts')}
      onReports={() => navigate('/reports')}
      onSettings={() => navigate('/settings')} onHelp={() => navigate('/help')} onSignOut={handleSignOut}
      onUnarchive={projectsHook.unarchiveProject}
      onDeleteArchived={projectsHook.deleteArchivedProject}
      onNavigate={(type, id, tab, filter) => {
        if (type === "overdue-blocked") navigate('/reports/overdue-blocked')
        else if (type === "vendor-insurance") navigate('/reports/vendor-insurance')
        else if (type === "budget-risk") navigate('/reports/budget-risk')
        else if (type === "budget-detail" && id) navigate(viewStateToPath('budget-detail', { projectId: id }))
        else if (type === "open-project" && id) navigate(viewStateToPath('project', { projectId: id, tab: tab || undefined, filter: filter || undefined }))
        else if (type === "reports") navigate('/reports')
      }} />
  )

  // --- All other views ---
  return (
    <Suspense fallback={suspenseFallback}>
      {effectiveView === "new-project" && (
        <NewProject onCancel={() => navigate('/')}
          onCreate={async (data, newContact, selectedTemplate) => {
            const id = await projectsHook.createProject(data, newContact, selectedTemplate)
            if (id) navigate(viewStateToPath('project', { projectId: id }))
          }}
          savedTemplates={templates.savedTemplates} contacts={contactsHook.contacts}
          session={auth.session} hasApiKey={settings.userSettings?.hasApiKey || false} />
      )}
      {effectiveView === "project" && projectsHook.activeProject && (() => {
        const projectRole = projectsHook.activeProject.role || "admin"
        if (projectRole === "contractor") return (
          <ContractorDashboard
            project={projectsHook.activeProject}
            session={auth.session}
            setProjects={projectsHook.setProjects}
            onBack={() => navigate('/')}
          />
        )
        if (projectRole === "stakeholder") return (
          <StakeholderView
            project={projectsHook.activeProject}
            onBack={() => navigate('/')}
          />
        )
        return (
          <ProjectView project={projectsHook.activeProject} session={auth.session}
            enabledFeatures={settings.enabledFeatures}
            defaultTab={params.tab || null} defaultFilter={params.filter || null}
            onBack={() => navigate('/')}
            onTabChange={tab => replace(viewStateToPath('project', { projectId: params.projectId, tab }))}
            backLabel={"\u2190 Dashboard"}
            onUpdate={u => projectsHook.updateProject(params.projectId, u)}
            onUpdateLocal={u => projectsHook.updateProjectLocal(params.projectId, u)}
            onDelete={() => { projectsHook.deleteProject(params.projectId); navigate('/'); }}
            onArchive={() => { projectsHook.archiveProject(params.projectId); navigate('/'); }}
            onBudgetDetail={() => navigate(viewStateToPath('budget-detail', { projectId: params.projectId }))}
            onGantt={() => navigate(viewStateToPath('gantt', { projectId: params.projectId }))}
            onDocumentation={() => navigate(viewStateToPath('project-documentation', { projectId: params.projectId }))}
            contacts={contactsHook.contacts} setContacts={contactsHook.setContacts}
            addTaskToSupabase={projectsHook.addTaskToSupabase} saveContactToSupabase={contactsHook.saveContactToSupabase} deleteTaskFromSupabase={projectsHook.deleteTaskFromSupabase}
            addTaskCost={projectsHook.addTaskCost} updateTaskCost={projectsHook.updateTaskCost} deleteTaskCost={projectsHook.deleteTaskCost}
            addChangeOrder={projectsHook.addChangeOrder} updateChangeOrder={projectsHook.updateChangeOrder} deleteChangeOrder={projectsHook.deleteChangeOrder} approveChangeOrder={projectsHook.approveChangeOrder}
            userProfile={settings.userProfile} companyProfile={settings.companyProfile}
            hasApiKey={settings.userSettings?.hasApiKey || false}
            subscriptionTier={settings.userSettings?.subscriptionTier || "pro"}
            seatAddonCount={settings.userSettings?.seatAddonCount || 0}
            onAddSeat={settings.incrementSeatAddonCount}
            onSaveAsTemplate={async (project) => {
              const tmpl = await templates.saveProjectAsTemplate(project);
              if (tmpl) {
                templates.setTemplateFromProject(tmpl);
                templates.setEditingTemplateId(null);
                navigate('/templates/builder');
              }
            }} />
        )
      })()}
      {effectiveView === "contacts" && (
        <ContactDirectory contacts={contactsHook.contacts} setContacts={contactsHook.setContacts} onBack={() => navigate('/')}
          saveContactToSupabase={contactsHook.saveContactToSupabase} deleteContactFromSupabase={contactsHook.deleteContactFromSupabase} />
      )}
      {effectiveView === "reports" && (
        <ReportsHub onBack={() => navigate('/')} onNavigate={(type, id, tab) => {
          if (type === "open-project" && id) navigate(viewStateToPath('project', { projectId: id, tab: tab || undefined }))
          else if (type === "change-orders-report") navigate(viewStateToPath('change-orders-report', { filter: tab || undefined }))
          else navigate(viewStateToPath(type, {}))
        }} projects={projectsHook.projects} contacts={contactsHook.contacts}
          enabledFeatures={settings.enabledFeatures} />
      )}
      {effectiveView === "portfolio-report" && (
        <PortfolioReport projects={projectsHook.projects} onBack={() => navigate('/reports')}
          onOpenProject={id => navigate(viewStateToPath('project', { projectId: id }))} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "vendor-insurance" && (
        <VendorInsuranceReport contacts={contactsHook.contacts} onBack={() => navigate('/reports')} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "overdue-blocked" && (
        <OverdueBlockedReport projects={projectsHook.projects} onBack={() => navigate('/reports')}
          onOpenProject={id => navigate(viewStateToPath('project', { projectId: id }))} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "change-orders-report" && (
        <ChangeOrdersReport
          projects={projectsHook.projects}
          initialFilter={params.filter || null}
          onBack={() => navigate('/reports')}
          onOpenProject={(id, tab) => navigate(viewStateToPath('project', { projectId: id, tab: tab || undefined }))}
          approveChangeOrder={projectsHook.approveChangeOrder}
          companyProfile={settings.companyProfile}
        />
      )}
      {effectiveView === "project-report" && projectsHook.activeProject && (
        <ProjectReport project={projectsHook.activeProject} onBack={() => navigate(viewStateToPath('project', { projectId: params.projectId }))} onOpenTask={() => navigate(viewStateToPath('project', { projectId: params.projectId, tab: 'tasks' }))} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "project-documentation" && projectsHook.activeProject && (
        <ProjectDocumentation project={projectsHook.activeProject} onBack={() => navigate(viewStateToPath('project', { projectId: params.projectId }))} />
      )}
      {effectiveView === "budget-detail" && projectsHook.activeProject && (
        <BudgetDetailReport project={projectsHook.activeProject} onBack={() => navigate(viewStateToPath('project', { projectId: params.projectId }))} onOpenTask={() => navigate(viewStateToPath('project', { projectId: params.projectId, tab: 'tasks' }))} hasApiKey={settings.userSettings?.hasApiKey || false} session={auth.session} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "gantt" && projectsHook.activeProject && (
        <GanttView project={projectsHook.activeProject} onBack={() => navigate(viewStateToPath('project', { projectId: params.projectId }))}
          onUpdate={u => projectsHook.updateProject(params.projectId, u)} />
      )}
      {effectiveView === "budget-risk" && (
        <BudgetRiskReport projects={projectsHook.projects} projectGroups={settings.projectGroups} onBack={() => navigate('/reports')}
          onOpenProject={id => navigate(viewStateToPath('project', { projectId: id }))} companyProfile={settings.companyProfile} />
      )}
      {effectiveView === "template-builder" && (
        <TemplateBuilder
          template={templates.editingTemplateId ? templates.savedTemplates.find(t => t.id === templates.editingTemplateId) || null : templates.templateFromProject || null}
          defaultProjectType={templates.newTemplateType}
          hasApiKey={settings.userSettings?.hasApiKey || false}
          session={auth.session}
          onBack={() => {
            templates.setEditingTemplateId(null);
            templates.setTemplateFromProject(null);
            window.history.back();
          }}
          onSave={async (tmpl) => {
            await templates.saveTemplateToSupabase(tmpl);
            templates.setEditingTemplateId(null);
            templates.setTemplateFromProject(null);
            window.history.back();
          }}
        />
      )}
      {effectiveView === "settings" && (
        <SettingsPage
          onBack={() => navigate('/')}
          onExport={() => exportData(projectsHook.projects, contactsHook.contacts, templates.savedTemplates)}
          onImport={projectsHook.handleImport}
          projectCount={projectsHook.projects.length}
          contactCount={contactsHook.contacts.length}
          onSignOut={handleSignOut}
          savedTemplates={templates.savedTemplates}
          onEditTemplate={(id) => { templates.setEditingTemplateId(id); navigate('/templates/builder'); }}
          onNewTemplate={(projectType) => { templates.setEditingTemplateId(null); templates.setNewTemplateType(projectType); navigate('/templates/builder'); }}
          onDeleteTemplate={templates.deleteTemplateFromSupabase}
          onDuplicateTemplate={templates.duplicateTemplate}
          onSetDefaultTemplate={templates.setTemplateDefault}
          companyProfile={settings.companyProfile}
          onSaveCompanyProfile={settings.saveCompanyProfile}
          userProfile={settings.userProfile}
          onSaveUserProfile={settings.saveUserProfile}
          userSettings={settings.userSettings}
          onSaveUserSettings={settings.saveUserSettings}
          enabledFeatures={settings.enabledFeatures}
          session={auth.session}
          projects={projectsHook.projects}
          onAddSeat={settings.incrementSeatAddonCount}
        />
      )}
      {effectiveView === "help" && (
        <HelpPage onBack={() => navigate('/')} />
      )}
    </Suspense>
  )
}
